// ============================================================
// AccidentGroupPanel.tsx — (Batch 4) ACCIDENT GROUPING on the case page.
//
// Two parts, deliberately separate:
//   1. CONFIRMED — the cases a person has confirmed arose from the same
//      accident, under a "Same accident" pill. Only this ever shows the pill.
//   2. SUGGESTED — "Case X might be the same accident — link?" from the
//      server heuristic (date ±1 day + a corroborating location / reg /
//      defendant signal), each with its reasons and Link / Not the same.
//      A suggestion is NEVER shown as a pill and never auto-links.
// Restricted group members render locked (no name/ref), matching the
// show-but-lock rule everywhere else.
// ============================================================
import React, { useCallback, useEffect, useState } from 'react'
import {
  getAccidentGroupApi, getAccidentSuggestionsApi, linkAccidentGroupApi,
  unlinkAccidentGroupApi, dismissAccidentSuggestionApi,
  type AccidentCaseRef, type AccidentSuggestion,
} from '../../api/casevoApi'
import { LockIcon, LOCKED_CASE_TIP } from '../shared/CaseLink'
import { formatDateUK } from '../../utils/caseHelpers'

export const SameAccidentPill: React.FC<{ count?: number; className?: string }> = ({ count, className = '' }) => (
  <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] px-2 py-[3px] rounded-[4px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] ${className}`}
    title={count && count > 1 ? `Confirmed: ${count} cases from the same accident` : 'Confirmed same accident'}>
    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
    Same accident{count && count > 1 ? ` · ${count}` : ''}
  </span>
)

const fmtDate = (d?: string | null): string => (d ? formatDateUK(d) : '')

const CaseLine: React.FC<{ c: AccidentCaseRef; onOpen?: (id: string) => void }> = ({ c, onOpen }) =>
  c.restricted ? (
    <span className="inline-flex items-center gap-1.5 text-casevo-text-soft" title={LOCKED_CASE_TIP}>
      <LockIcon className="w-3.5 h-3.5" />Restricted case
    </span>
  ) : (
    <span className="min-w-0">
      {onOpen
        ? <button type="button" onClick={() => onOpen(c.id)} className="font-medium text-casevo-forest hover:underline underline-offset-2 focus:outline-none casevo-focus">{c.name}</button>
        : <span className="font-medium text-casevo-forest">{c.name}</span>}
      <span className="ml-2 font-mono text-[11px] text-casevo-text-soft">{c.ref}</span>
      {c.clientName && <span className="ml-2 text-[12px] text-casevo-text-soft">· {c.clientName}</span>}
    </span>
  )

// onLoaded: fired once the first load has answered (the parent holds the
// content below this panel until then, so the panel can't push it down).
const AccidentGroupPanel: React.FC<{ caseId: string; onOpenCase?: (id: string) => void; onLoaded?: () => void }> = ({ caseId, onOpenCase, onLoaded }) => {
  const [group, setGroup] = useState<AccidentCaseRef[] | null>(null)
  const [suggestions, setSuggestions] = useState<AccidentSuggestion[] | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [g, s] = await Promise.all([getAccidentGroupApi(caseId), getAccidentSuggestionsApi(caseId)])
      setGroup(g.cases)
      setSuggestions(s)
    } catch {
      setGroup([])
      setSuggestions([])
      setError('Couldn’t load same-accident cases.')
    }
  }, [caseId])
  useEffect(() => { void load() }, [load])
  const loaded = group !== null && suggestions !== null
  useEffect(() => { if (loaded) onLoaded?.() }, [loaded]) // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id: string, fn: () => Promise<unknown>): Promise<void> => {
    setBusyId(id)
    setError(null)
    try { await fn(); await load() } catch (e) { setError(e instanceof Error ? e.message : 'That didn’t work — please try again.') } finally { setBusyId(null) }
  }

  if (group === null || suggestions === null) return null
  const others = group.filter((c) => c.id !== caseId)
  if (others.length === 0 && suggestions.length === 0 && !error) return null

  return (
    <div className="mb-4 rounded-[12px] border border-casevo-border bg-casevo-surface p-3">
      {others.length > 0 && (
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-2">
            <SameAccidentPill count={group.length} />
            <span className="text-[12px] text-casevo-text-soft">Confirmed — these cases arose from the same accident.</span>
            <button type="button" disabled={busyId === 'self'} onClick={() => void act('self', () => unlinkAccidentGroupApi(caseId))}
              className="ml-auto text-[11.5px] text-casevo-text-soft hover:text-casevo-danger focus:outline-none casevo-focus disabled:opacity-50">
              Remove this case from the group
            </button>
          </div>
          <ul className="flex flex-col gap-1.5">
            {others.map((c) => <li key={c.id} className="text-[13px]"><CaseLine c={c} onOpen={onOpenCase} /></li>)}
          </ul>
        </div>
      )}
      {suggestions.length > 0 && (
        <div className={others.length > 0 ? 'mt-3 pt-3 border-t border-casevo-border-soft' : ''}>
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-casevo-text-soft mb-2">Possibly the same accident</div>
          <ul className="flex flex-col gap-2">
            {suggestions.map((s) => (
              <li key={s.id} className="flex items-start gap-3 text-[13px]">
                <div className="flex-1 min-w-0">
                  <div><CaseLine c={s} onOpen={onOpenCase} /></div>
                  <div className="text-[11.5px] text-casevo-text-soft mt-0.5">
                    {fmtDate(s.accidentDate)}{s.accidentDate ? ' · ' : ''}{s.reasons.join(' · ')}
                    {s.inAnotherGroup && ' · already grouped with other cases (linking merges the groups)'}
                  </div>
                </div>
                <button type="button" disabled={busyId === s.id} onClick={() => void act(s.id, () => linkAccidentGroupApi(caseId, s.id))}
                  className="shrink-0 text-[12px] font-semibold text-casevo-brand border border-casevo-border rounded-[8px] px-2.5 py-1 hover:border-casevo-mid focus:outline-none casevo-focus disabled:opacity-50">
                  Same accident
                </button>
                <button type="button" disabled={busyId === s.id} onClick={() => void act(s.id, () => dismissAccidentSuggestionApi(caseId, s.id))}
                  className="shrink-0 text-[12px] text-casevo-text-soft hover:text-casevo-forest px-1.5 py-1 focus:outline-none casevo-focus disabled:opacity-50">
                  Not the same
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <div role="alert" className="mt-2 text-[12px] text-casevo-danger">{error}</div>}
    </div>
  )
}

export default AccidentGroupPanel
